import { useState } from 'react'
import type { CVDocument } from '../../stores/cvGenerationStore'

interface MobileCardEditorProps {
  cvDoc: CVDocument
  onChange: (doc: CVDocument) => void
  onPreview: () => void
}

// ── helpers ──────────────────────────────────────────────────────────────────

function getSummaryText(doc: CVDocument): string {
  const sec = doc.sections.find((s) => s.type === 'SUMMARY')
  if (!sec) return ''
  return String((sec.content as Record<string, unknown>).text ?? '')
}

function setSummaryText(doc: CVDocument, text: string): CVDocument {
  return {
    ...doc,
    sections: doc.sections.map((s) =>
      s.type === 'SUMMARY' ? { ...s, content: { ...s.content, text } } : s,
    ),
  }
}

interface Role {
  title: string
  employer: string
  period: string
  description?: string
  projects: Project[]
}

interface Project {
  name?: string
  description?: string
  bullets: string[]
}

interface SkillCategory {
  label: string
  skills: string[]
}

function getRoles(doc: CVDocument): Role[] {
  const sec = doc.sections.find((s) => s.type === 'EXPERIENCE')
  if (!sec) return []
  return ((sec.content as Record<string, unknown>).roles as Role[]) ?? []
}

function setRoles(doc: CVDocument, roles: Role[]): CVDocument {
  return {
    ...doc,
    sections: doc.sections.map((s) =>
      s.type === 'EXPERIENCE'
        ? { ...s, content: { ...s.content, roles } }
        : s,
    ),
  }
}

function getSkillCategories(doc: CVDocument): SkillCategory[] {
  const sec = doc.sections.find((s) => s.type === 'SKILLS')
  if (!sec) return []
  return (
    ((sec.content as Record<string, unknown>).categories as SkillCategory[]) ??
    []
  )
}

function setSkillCategories(
  doc: CVDocument,
  categories: SkillCategory[],
): CVDocument {
  return {
    ...doc,
    sections: doc.sections.map((s) =>
      s.type === 'SKILLS'
        ? { ...s, content: { ...s.content, categories } }
        : s,
    ),
  }
}

function autoResize(el: HTMLTextAreaElement) {
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

// ── MobileSection accordion ───────────────────────────────────────────────────

interface MobileSectionProps {
  title: string
  children: React.ReactNode
  badge?: string
}

function MobileSection({ title, children, badge }: MobileSectionProps) {
  const [open, setOpen] = useState(false)
  return (
    <div className="bg-white border border-[#dac2b6] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[#545f6e] text-base">
            {open ? 'expand_less' : 'expand_more'}
          </span>
          <span className="text-[10px] font-semibold text-[#545f6e] uppercase tracking-widest">
            {title}
          </span>
        </div>
        {badge && (
          <span className="text-[10px] text-[#877369]">{badge}</span>
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#dac2b6]/30">{children}</div>
      )}
    </div>
  )
}

// ── sub-components ────────────────────────────────────────────────────────────

interface BulletListProps {
  bullets: string[]
  onChange: (bullets: string[]) => void
}

function BulletList({ bullets, onChange }: BulletListProps) {
  const move = (i: number, dir: -1 | 1) => {
    const next = [...bullets]
    const j = i + dir
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      {bullets.map((bullet, i) => (
        <div key={i} className="flex items-stretch gap-1.5">
          <textarea
            value={bullet}
            onChange={(e) => {
              autoResize(e.target)
              const next = [...bullets]
              next[i] = e.target.value
              onChange(next)
            }}
            onInput={(e) => autoResize(e.currentTarget)}
            className="flex-grow border border-[#dac2b6] rounded px-2 py-1 text-sm resize-none min-h-[80px] focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]"
          />
          <div className="flex flex-col justify-between flex-shrink-0 py-0.5">
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="p-0.5 hover:bg-[#e5eeff] rounded disabled:opacity-30"
              aria-label="Move bullet up"
            >
              <span className="material-symbols-outlined text-[16px]">
                arrow_drop_up
              </span>
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === bullets.length - 1}
              className="p-0.5 hover:bg-[#e5eeff] rounded disabled:opacity-30"
              aria-label="Move bullet down"
            >
              <span className="material-symbols-outlined text-[16px]">
                arrow_drop_down
              </span>
            </button>
            <button
              type="button"
              onClick={() => onChange(bullets.filter((_, idx) => idx !== i))}
              className="p-0.5 hover:bg-[#e5eeff] rounded"
              aria-label="Remove bullet"
            >
              <span className="material-symbols-outlined text-[16px] text-[#ba1a1a]">
                close
              </span>
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...bullets, ''])}
        className="text-[#833c0b] font-semibold text-sm mt-1"
      >
        ＋ Add bullet
      </button>
    </div>
  )
}

interface ExperienceCardProps {
  role: Role
  roleIndex: number
  onChange: (role: Role) => void
}

function ExperienceCard({ role, roleIndex, onChange }: ExperienceCardProps) {
  const updateProject = (pi: number, updated: Project) => {
    const projects = role.projects.map((p, i) => (i === pi ? updated : p))
    onChange({ ...role, projects })
  }

  return (
    <div className="space-y-3 pt-3">
      <div className="text-[10px] font-semibold text-[#545f6e] uppercase tracking-widest">
        Role {roleIndex + 1}
      </div>

      {/* Role header fields */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={role.title}
            onChange={(e) => onChange({ ...role, title: e.target.value })}
            placeholder="Job title"
            className="w-2/3 border border-[#dac2b6] rounded px-2 py-1 text-sm font-semibold focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]"
          />
          <input
            type="text"
            value={role.employer}
            onChange={(e) => onChange({ ...role, employer: e.target.value })}
            placeholder="Company"
            className="w-1/3 border border-[#dac2b6] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]"
          />
        </div>
        <input
          type="text"
          value={role.period}
          onChange={(e) => onChange({ ...role, period: e.target.value })}
          placeholder="e.g. Jan 2021 – Present"
          className="w-full border border-[#dac2b6] rounded px-2 py-1 text-sm text-[#545f6e] focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]"
        />
      </div>

      {/* Projects */}
      <div className="space-y-4">
        {role.projects.map((proj, pi) => (
          <div
            key={pi}
            className="pl-3 border-l-2 border-[#833c0b] space-y-2"
          >
            {/* Project name */}
            <input
              type="text"
              value={proj.name ?? ''}
              onChange={(e) =>
                updateProject(pi, {
                  ...proj,
                  name: e.target.value || undefined,
                })
              }
              placeholder="Initiative name (optional)"
              className="w-full border-b border-[#dac2b6] text-sm font-medium focus:outline-none focus:border-[#833c0b] rounded-none px-0 bg-transparent"
            />
            {/* Project description */}
            <input
              type="text"
              value={proj.description ?? ''}
              onChange={(e) =>
                updateProject(pi, {
                  ...proj,
                  description: e.target.value || undefined,
                })
              }
              placeholder="Brief description (optional)"
              className="w-full italic text-[#545f6e] text-sm bg-transparent border-none focus:outline-none focus:ring-0 p-0"
            />
            {/* Bullets */}
            <BulletList
              bullets={proj.bullets}
              onChange={(bullets) => updateProject(pi, { ...proj, bullets })}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

interface SkillCategoryCardProps {
  category: SkillCategory
  onUpdate: (cat: SkillCategory) => void
  onRemove: () => void
  addInputValue: string
  onAddInputChange: (v: string) => void
  onAddSkill: () => void
}

function SkillCategoryCard({
  category,
  onUpdate,
  onRemove,
  addInputValue,
  onAddInputChange,
  onAddSkill,
}: SkillCategoryCardProps) {
  return (
    <div className="space-y-2 pt-3">
      <div className="flex items-center justify-between">
        <input
          type="text"
          value={category.label}
          onChange={(e) => onUpdate({ ...category, label: e.target.value })}
          className="border-b border-[#dac2b6] font-medium text-sm focus:outline-none focus:border-[#833c0b] rounded-none w-1/2 p-0 bg-transparent"
        />
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-[#ba1a1a] hover:underline"
        >
          Remove
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {category.skills.map((skill, si) => (
          <span
            key={si}
            className="bg-[#003d60] text-white text-[10px] font-semibold px-2 py-1 rounded flex items-center gap-1 uppercase tracking-wider"
          >
            {skill}
            <button
              type="button"
              onClick={() =>
                onUpdate({
                  ...category,
                  skills: category.skills.filter((_, idx) => idx !== si),
                })
              }
              aria-label={`Remove ${skill}`}
            >
              <span className="material-symbols-outlined text-[12px]">
                close
              </span>
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="text"
          value={addInputValue}
          onChange={(e) => onAddInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAddSkill()
            }
          }}
          placeholder="Skill name"
          className="border border-[#dac2b6] rounded px-2 py-1 text-sm w-1/3 focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]"
        />
        <button
          type="button"
          onClick={onAddSkill}
          className="text-[#833c0b] font-semibold text-sm"
        >
          Add
        </button>
      </div>
    </div>
  )
}

// ── main component ────────────────────────────────────────────────────────────

export default function MobileCardEditor({
  cvDoc,
  onChange,
  onPreview,
}: MobileCardEditorProps) {
  // Per-category new-skill input state
  const categories = getSkillCategories(cvDoc)
  const [skillInputs, setSkillInputs] = useState<string[]>(() =>
    categories.map(() => ''),
  )

  // Keep skillInputs length in sync if categories change
  const syncedInputs =
    skillInputs.length === categories.length
      ? skillInputs
      : categories.map((_, i) => skillInputs[i] ?? '')

  const roles = getRoles(cvDoc)

  const handleRoleChange = (i: number, role: Role) => {
    const next = roles.map((r, idx) => (idx === i ? role : r))
    onChange(setRoles(cvDoc, next))
  }

  const handleCategoryUpdate = (i: number, cat: SkillCategory) => {
    const next = categories.map((c, idx) => (idx === i ? cat : c))
    onChange(setSkillCategories(cvDoc, next))
  }

  const handleCategoryRemove = (i: number) => {
    const next = categories.filter((_, idx) => idx !== i)
    const nextInputs = syncedInputs.filter((_, idx) => idx !== i)
    setSkillInputs(nextInputs)
    onChange(setSkillCategories(cvDoc, next))
  }

  const handleAddSkill = (i: number) => {
    const val = syncedInputs[i]?.trim()
    if (!val) return
    const cat = categories[i]
    handleCategoryUpdate(i, { ...cat, skills: [...cat.skills, val] })
    const nextInputs = [...syncedInputs]
    nextInputs[i] = ''
    setSkillInputs(nextInputs)
  }

  const handleAddCategory = () => {
    onChange(
      setSkillCategories(cvDoc, [...categories, { label: 'New Category', skills: [] }]),
    )
    setSkillInputs([...syncedInputs, ''])
  }

  // Education / Languages (read-only)
  const educationEntries = cvDoc.user.education ?? []
  const languageEntries = cvDoc.user.languages ?? []
  const educationSec = cvDoc.sections.find((s) => s.type === 'EDUCATION')
  const languagesSec = cvDoc.sections.find((s) => s.type === 'LANGUAGES')

  const roleBadge = `${roles.length} role${roles.length !== 1 ? 's' : ''}`
  const catBadge = `${categories.length} categor${categories.length !== 1 ? 'ies' : 'y'}`

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div
        id="mobile-card-editor-scroll"
        className="flex-1 overflow-y-auto"
        style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        <div className="px-4 pt-4 pb-4 space-y-3">
          {/* ── About Me ── */}
          <MobileSection title="About Me">
            <textarea
              value={getSummaryText(cvDoc)}
              rows={8}
              onChange={(e) => {
                autoResize(e.target)
                onChange(setSummaryText(cvDoc, e.target.value))
              }}
              onInput={(e) => autoResize(e.currentTarget)}
              placeholder="Describe your professional background…"
              className="w-full border border-[#dac2b6] rounded p-2 text-sm resize-none mt-3 focus:outline-none focus:border-[#833c0b] focus:ring-1 focus:ring-[#833c0b]"
            />
          </MobileSection>

          {/* ── Professional Experience ── */}
          <MobileSection title="Professional Experience" badge={roleBadge}>
            <div className="divide-y divide-[#dac2b6]/40">
              {roles.map((role, i) => (
                <ExperienceCard
                  key={i}
                  role={role}
                  roleIndex={i}
                  onChange={(r) => handleRoleChange(i, r)}
                />
              ))}
            </div>
          </MobileSection>

          {/* ── Core Competencies ── */}
          <MobileSection title="Core Competencies" badge={catBadge}>
            <div className="divide-y divide-[#dac2b6]/40">
              {categories.map((cat, i) => (
                <SkillCategoryCard
                  key={i}
                  category={cat}
                  onUpdate={(c) => handleCategoryUpdate(i, c)}
                  onRemove={() => handleCategoryRemove(i)}
                  addInputValue={syncedInputs[i] ?? ''}
                  onAddInputChange={(v) => {
                    const next = [...syncedInputs]
                    next[i] = v
                    setSkillInputs(next)
                  }}
                  onAddSkill={() => handleAddSkill(i)}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleAddCategory}
              className="text-[#833c0b] font-semibold text-sm mt-3"
            >
              + Add category
            </button>
          </MobileSection>

          {/* ── Education (read-only) ── */}
          {educationSec && educationEntries.length > 0 && (
            <MobileSection
              title="Education"
              badge={`${educationEntries.length} entr${educationEntries.length !== 1 ? 'ies' : 'y'}`}
            >
              <div className="space-y-1 mt-3">
                {educationEntries.map((edu, i) => (
                  <p key={i} className="text-sm text-[#121c28]">
                    <strong>{edu.degree}</strong>
                    {edu.degree && edu.institution ? ', ' : ''}
                    {edu.institution}
                    {edu.year && (
                      <span className="text-[#545f6e]"> · {edu.year}</span>
                    )}
                  </p>
                ))}
              </div>
            </MobileSection>
          )}

          {/* ── Languages (read-only) ── */}
          {languagesSec && languageEntries.length > 0 && (
            <MobileSection
              title="Languages"
              badge={`${languageEntries.length}`}
            >
              <p className="text-sm text-[#121c28] mt-3">
                {languageEntries.join(' · ')}
              </p>
            </MobileSection>
          )}
        </div>
      </div>

      {/* Bottom bar — flex child, not fixed */}
      <div className="flex-shrink-0 bg-white border-t border-[#dac2b6] p-4">
        <button
          type="button"
          onClick={onPreview}
          className="w-full bg-[#833c0b] text-white py-3 px-6 font-bold uppercase tracking-widest flex justify-center items-center gap-2 active:scale-[0.98] transition-transform rounded"
        >
          Preview CV
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      </div>
    </div>
  )
}
